import React, { useState, useEffect } from "react";
import {
  Grid,
  Typography,
  Box,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Button,
  Tooltip,
  Paper,
  AppBar,
  Toolbar,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import ResumeTemplates from "./dashboard/ResumeTemplates";
import CareerCheatSheets from "./dashboard/CheatSheets";
import MidCareerSwitchingDashboard from "./dashboard/MidCareerSwitching";
import LandDreamJobDashboard from "./dashboard/LandYourDreamJob";
import SpecialCoaching from "./dashboard/SpecialCoaching";
import Report from "./Report";
import { useParams } from "react-router-dom";
import SummaryPage from "./SummaryPage";
import CallMadeIcon from "@mui/icons-material/CallMade";
import BenefitsComparison from "./BenefitsComparison";
import NavigationSidebar from "./NavigationSidebar";
import AIChatWidget from "./ai/AIChatWidget";
import axios from "axios";
import CareerTransitions from "./dashboard/CareerTransitions";

// Define minimum tiers for dashboard cards
// 0 = Free, 1 = Standard, 2 = Platinum
const cardDataConfig = [
  {
    title: "Ask CoachCAROL",
    description:
      "Ask CoachCAROL your career questions and get personalised answers",
    image: "/images/Dashboard/CoachCAROL dashboard.png",
    pathSuffix: "ai-chat",
    buttonText: "Ask CoachCAROL",
    component: AIChatWidget,
    minTier: 0,
    cardColor: "#F2F6FF",
  },
  {
    title: "My Career Report",
    description:
      "Your personalized career report to help you understand your career goals and aspirations",
    image: "/images/Dashboard/Career Analysis Graphic.png",
    pathSuffix: "report",
    buttonText: "View Report",
    component: Report,
    minTier: 0,
    cardColor: "#F2F6FF",
  },
  {
    title: "My Career Summary",
    description: "Get a one page summary of your career report",
    image: "/images/Dashboard/Designer.png",
    pathSuffix: "summary",
    buttonText: "View Career Summary",
    component: SummaryPage,
    minTier: 0,
    cardColor: "#E1F5F6",
  },
  {
    title: "Job Hunting Toolkit",
    description:
      "Here are some useful short-cuts and tools to help you develop your Career Strategy and to make better-informed decisions",
    image: "/images/Dashboard/Toolkit Image.png",
    pathSuffix: "cheat-sheets",
    buttonText: "View Resources",
    component: CareerCheatSheets,
    minTier: 0,
    cardColor: "#F9F3D8",
  },
  {
    title: "Making Your Mid-Career Switch (Online Course)",
    description:
      "Mid-Career Switches can be challenging, complicated and risky. Learn how to transition effectively using our proprietary 5-step Framework",
    image: "/images/Dashboard/MidCareer Switch Image.png",
    pathSuffix: "mid-career-switching",
    buttonText: "Learn More",
    component: MidCareerSwitchingDashboard,
    minTier: 1,
    cardColor: "#FFF4F1",
  },
  {
    title: "Landing Your Dream Job (Online Course)",
    description:
      "In a challenging job market where great opportunities are scarce and competition is tough, learn how you can improve your chances of landing your dream job here",
    image: "/images/Dashboard/Land Your Dream Job.png",
    pathSuffix: "land-your-dream-job",
    buttonText: "Get Started",
    component: LandDreamJobDashboard,
    minTier: 1,
    cardColor: "#FBFBE8",
  },
  {
    title: "Career Transitions",
    description:
      "Learn how to navigate career transitions and changes in your career",
    image: "/images/Dashboard/Career Transitions.png",
    pathSuffix: "career-transitions",
    buttonText: "Get Started",
    component: CareerTransitions,
    minTier: 0,
    cardColor: "#FBFBE8",
  },

  {
    title: "Resume Samples",
    description:
      "Download Resume samples with content that is tailored to your industry or function",
    image: "/images/Dashboard/Resume Templates Image.png",
    pathSuffix: "resume-templates",
    buttonText: "View Templates",
    component: ResumeTemplates,
    minTier: 1,
    cardColor: "#F7FFE4",
  },
  {
    title: "Personalised Coaching",
    description: "Get personalized career coaching at exclusive member rates",
    image: "/images/Dashboard/Personalised Coaching.png",
    pathSuffix: "special-coaching",
    buttonText: "Learn More",
    component: SpecialCoaching,
    minTier: 0,
    cardColor: "#F9E8FB",
  },
  // Consider adding an AI Chat card if desired, requires minTier 2
  // {
  //   title: "Ask Caroline (AI Chat)",
  //   description: "Chat with our AI assistant for career advice.",
  //   image: "/path/to/ai-chat-image.png",
  //   pathSuffix: "ai-chat",
  //   buttonText: "Start Chatting",
  //   component: AIChatWidget, // Or navigate to a dedicated page
  //   minTier: 2,
  //   cardColor: "#E0F7FA",
  // },
];

const CareerDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userId } = useParams();

  // Remove userTier, isLoadingStatus, and upgrade-status logic
  const [username, setUsername] = useState("Guest");
  const [userProfile, setUserProfile] = useState(null);

  useEffect(() => {
    // Fetch user profile (Keep this logic)
    const fetchUserProfile = async () => {
      if (!userId) return;
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/user/${userId}/profile-data`
        );
        const data = await response.json();
        if (data.success) {
          setUserProfile(data.profile);
          setUsername(data.profile?.name || "Guest");
        }
      } catch (error) {
        console.error("Error fetching user profile:", error);
      }
    };
    fetchUserProfile();
  }, [userId]);

  // Dashboard Card Component - All cards always accessible
  const DashboardCard = ({
    title,
    description,
    image,
    pathSuffix,
    buttonText,
    cardColor,
  }) => {
    return (
      <Card
        sx={{
          display: "flex",
          flexDirection: "column",
          borderRadius: 3,
          p: 3,
          width: "100%",
          height: "100%",
        }}
      >
        <Box
          sx={{
            position: "relative",
            height: 200,
            backgroundColor: cardColor,
            borderRadius: 5,
          }}
        >
          <Box
            sx={{
              position: "absolute",
              left: "5%",
              width: "auto",
              height: "100%",
              paddingTop: "3%",
              paddingBottom: "3%",
              boxSizing: "border-box",
              display: "flex",
              alignItems: "center",
              "& img": {
                width: "100%",
                height: "90%",
                objectFit: "contain",
                borderRadius: 10,
              },
            }}
          >
            <img src={image} alt={title} />
          </Box>
        </Box>
        <CardContent sx={{ flexGrow: 1 }}>
          <Typography gutterBottom variant="h5" component="div">
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        </CardContent>
        <CardActions
          sx={{
            mt: "auto",
            pt: 2,
            borderTop: "1px solid rgba(0, 0, 0, 0.12)",
            minHeight: "48px",
            px: 1,
          }}
        >
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Button
              size="small"
              variant="contained"
              onClick={() => navigate(`/${pathSuffix}/${userId}`)}
              endIcon={<CallMadeIcon />}
              sx={{ backgroundColor: "#000000", height: "36px" }}
            >
              {buttonText}
            </Button>
          </Box>
        </CardActions>
      </Card>
    );
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Box sx={{ display: "flex" }}>
        <NavigationSidebar userId={userId} />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            width: { sm: `calc(100% - ${0}px)` },
            ml: { sm: `${0}px` },
          }}
        >
          <AppBar
            position="static"
            className="menu-bar"
            elevation={0}
            sx={{ borderRadius: 20, marginBottom: 1, backgroundColor: "#fff" }}
          >
            <Toolbar variant="regular">
              <IconButton edge="start" color="inherit" aria-label="menu">
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                  }}
                >
                  <Typography
                    component="span"
                    sx={{
                      fontFamily: "Merriweather, serif",
                      fontWeight: 700,
                      fontSize: { xs: "1.8rem", md: "32px" },
                      lineHeight: 1.1,
                      color: "#000000",
                    }}
                  >
                    Coach Carol
                  </Typography>
                  <Typography
                    variant="caption"
                    component="div"
                    sx={{
                      fontFamily: "Poppins, sans-serif",
                      fontWeight: 400,
                      fontSize: "12px",
                      color: "#000000",
                    }}
                  >
                    Powered by Career Agility
                  </Typography>
                </Box>
              </IconButton>
            </Toolbar>
          </AppBar>

          <Box sx={{ flexGrow: 1, p: 3 }}>
            <Typography variant="h5" component="h1" gutterBottom>
              My Career Dashboard
            </Typography>
            <Typography variant="body1" component="h1" gutterBottom>
              Hello {username}!
            </Typography>
            <Typography
              variant="body1"
              component="h1"
              gutterBottom
              sx={{ mb: 6 }}
            >
              Welcome to your one-stop Career Resource Centre. Please explore
              the links to find out how you can further enhance your Career
              Success.
            </Typography>

            <Box
              sx={{
                backgroundColor: "#8DDAF0",
                margin: "0 -24px",
                padding: "0 24px",
              }}
            >
              <Grid
                container
                spacing={4}
                sx={{
                  paddingY: 4,
                  alignItems: "stretch",
                }}
              >
                {cardDataConfig.map((card) => (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={4}
                    key={card.title}
                    sx={{ display: "flex", mb: 6 }}
                  >
                    <DashboardCard {...card} />
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default CareerDashboard;
